/**
 * ч.1102: «Пошук помилок агентом» — окрема функція аудиту проводок.
 * Агент Oblik+/AI сам переглядає нетипові документи періоду (ручні операції,
 * рідкісна кореспонденція, найбільші суми) і шукає проводки, що не відповідають
 * змісту операції. Дорожче за пояснення знахідки, тому: окреме право, оцінка
 * вартості ДО запуску, виконання порціями з поступом і кнопкою «зупинити».
 * Знахідки зʼявляються в окремому блоці «Знахідки агента Oblik+/AI» нижче.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ToastProvider';
import {
  postingAudit, type AuditPeriod, type AuditScan, type ScanPreview,
} from '@/api/postingAudit';
import AiBadge from '@/shared/components/AiBadge';
import { extractApiError } from '@/utils/extractApiError';

const LIMITS = [10, 25, 50, 100];
const fmt = (v: string | number) => Number(v).toFixed(2);

interface Props {
  orgId: string;
  period: AuditPeriod;
  canScan: boolean;
  aiAvailable: string | null;
}

/** Період пошуку — до одного року (вартість росте з кількістю документів). */
export function periodTooLong(p: AuditPeriod): boolean {
  if (!p.date_from || !p.date_to) return true;
  return (Date.parse(p.date_to) - Date.parse(p.date_from)) / 86_400_000 > 366;
}

export default function PostingAuditScanPanel({ orgId, period, canScan, aiAvailable }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [limit, setLimit] = useState(25);
  const [includeImported, setIncludeImported] = useState(false);
  const [preview, setPreview] = useState<ScanPreview | null>(null);
  const [busy, setBusy] = useState<'preview' | 'run' | null>(null);
  const [scan, setScan] = useState<AuditScan | null>(null);
  const stopRef = useRef(false);

  const latestQ = useQuery({
    queryKey: ['posting-audit', 'scan-latest', orgId],
    queryFn: () => postingAudit.scanLatest(orgId),
    enabled: !!orgId,
  });
  useEffect(() => { setScan(latestQ.data ?? null); }, [latestQ.data]);
  // Інша організація чи період — стара оцінка вже не про них.
  useEffect(() => { setPreview(null); }, [orgId, period.date_from, period.date_to, limit, includeImported]);
  useEffect(() => () => { stopRef.current = true; }, []);

  const params = { organization_id: orgId, ...period, limit, include_imported: includeImported };
  const tooLong = periodTooLong(period);
  const noRight = 'Немає права «Oblik+/AI: пошук помилок у документах агентом» у цій організації';
  const lock = !canScan ? noRight : tooLong ? 'Пошук агентом — за період до одного року' : undefined;
  const onError = (e: unknown) => toast.show({ type: 'error', message: extractApiError(e) });

  const estimate = async () => {
    setBusy('preview');
    try { setPreview(await postingAudit.scanPreview(params)); } catch (e) { onError(e); } finally { setBusy(null); }
  };

  /** Кроки — доки пошук триває і користувач не зупинив; кожен крок оновлює знахідки. */
  const drive = async (start: AuditScan) => {
    setBusy('run');
    stopRef.current = false;
    let cur = start;
    setScan(cur);
    try {
      while (cur.status === 'RUNNING' && !stopRef.current) {
        cur = await postingAudit.scanStep(cur.id);
        setScan(cur);
        void qc.invalidateQueries({ queryKey: ['posting-audit', 'summary'] });
        void qc.invalidateQueries({ queryKey: ['posting-audit', 'findings'] });
      }
    } catch (e) {
      onError(e);
      void latestQ.refetch();
    } finally {
      setBusy(null);
      void qc.invalidateQueries({ queryKey: ['ai-balance'] });
    }
  };

  const run = async () => {
    if (!preview || !window.confirm(
      `Агент перевірить документів: ${preview.documents}. Орієнтовна вартість — до ${fmt(preview.estimate_credits)} `
      + 'кредитів Oblik+/AI. Запустити пошук?')) return;
    try {
      const started = await postingAudit.scanStart(params);
      setPreview(null);
      await drive(started);
    } catch (e) { onError(e); }
  };

  const stop = async () => {
    stopRef.current = true;
    if (scan) { try { setScan(await postingAudit.scanStop(scan.id)); } catch (e) { onError(e); } }
  };

  const active = scan && (scan.status === 'RUNNING' || scan.status === 'PAUSED');
  const r = preview?.by_reason;

  return (
    <div data-testid="audit-scan" style={{
      border: '1px solid var(--border)', borderRadius: 6, padding: '0.75rem 1rem',
      marginBottom: '1rem', background: 'var(--bg-soft-2)',
    }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <strong>Пошук помилок агентом<AiBadge /></strong>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem', marginLeft: 'auto' }}>
          доступно кредитів: {aiAvailable ?? '…'}
        </span>
      </div>
      <p style={{ margin: '0.35rem 0 0.6rem', color: 'var(--text-secondary)' }}>
        Агент Oblik+/AI сам переглядає нетипові документи періоду — ручні операції, рідкісні
        кореспонденції, найбільші суми — і шукає проводки, що не відповідають змісту операції.
        Витрачає кредити; документ із незміненими проводками вдруге не перевіряється.
      </p>

      {!active && (
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
            документів
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} aria-label="Скільки документів">
              {LIMITS.map((n) => <option key={n} value={n}>до {n}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}
            title="Проводки таких документів скопійовано з 1С без змін; за замовчуванням агент їх не розглядає">
            <input type="checkbox" checked={includeImported}
              onChange={(e) => setIncludeImported(e.target.checked)} />
            разом з перенесеними з 1С
          </label>
          <button type="button" disabled={!!lock || busy !== null} title={lock} onClick={() => void estimate()}>
            {busy === 'preview' ? 'рахуємо…' : 'оцінити вартість'}
          </button>
        </div>
      )}

      {preview && !active && (
        <div data-testid="audit-scan-preview" style={{ marginTop: '0.6rem' }}>
          {preview.documents === 0 ? (
            <span>
              Нових документів для перевірки за цей період немає.
              {preview.skipped_imported > 0
                && ` Перенесених з 1С: ${preview.skipped_imported} — позначте «разом з перенесеними з 1С», якщо їх теж треба переглянути.`}
            </span>
          ) : (
            <>
              <div>
                Буде перевірено документів: <b>{preview.documents}</b>
                {r && ` (ручні операції — ${r.manual}, рідкісна кореспонденція — ${r.rare}, великі суми — ${r.big})`}.
                Орієнтовна вартість — <b>до {fmt(preview.estimate_credits)} кредитів</b>.
              </div>
              <button type="button" className="primary" style={{ marginTop: '0.4rem' }}
                disabled={!!lock || busy !== null} title={lock} onClick={() => void run()}>
                запустити пошук
              </button>
            </>
          )}
        </div>
      )}

      {scan && (
        <div data-testid="audit-scan-state" style={{ marginTop: '0.6rem' }}>
          <div>
            {scan.status === 'RUNNING' && (busy === 'run' ? 'Пошук триває: ' : 'Пошук не завершено: ')}
            {scan.status === 'PAUSED' && 'Пошук призупинено: '}
            {scan.status === 'DONE' && 'Останній пошук завершено: '}
            {scan.status === 'STOPPED' && 'Останній пошук зупинено: '}
            перевірено <b>{scan.done}</b> з {scan.total}, знайдено <b>{scan.issues}</b>,
            списано <b>{fmt(scan.charged)}</b> кредитів
            <span style={{ color: 'var(--muted)', marginLeft: '0.4rem', fontSize: '0.85rem' }}>
              ({scan.date_from.split('-').reverse().join('.')}–{scan.date_to.split('-').reverse().join('.')})
            </span>
          </div>
          {scan.note && <div style={{ color: 'var(--badge-amber-fg)' }}>{scan.note}</div>}
          {scan.status === 'DONE' && scan.issues > 0 && (
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Знахідки — у блоці «Знахідки агента Oblik+/AI» нижче.
            </div>
          )}
          {active && (
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
              {busy !== 'run' && (
                <button type="button" className="primary" disabled={!canScan} title={canScan ? undefined : noRight}
                  onClick={() => void drive({ ...scan, status: 'RUNNING' })}>продовжити</button>
              )}
              <button type="button" disabled={!canScan} title={canScan ? 'Решту документів не буде перевірено; списане не повертається' : noRight}
                onClick={() => void stop()}>зупинити</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
